import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PiCaretDownDuotone, PiCheckDuotone, PiXDuotone } from 'react-icons/pi';

/**
 * Select — кастомный dropdown в стиле BetMate.
 *
 * @param {string}   label        — подпись над полем
 * @param {string}   error        — текст ошибки
 * @param {string}   placeholder  — текст при пустом значении
 * @param {string}   value        — текущее значение (id опции)
 * @param {Function} onChange     — (value: string) => void
 * @param {{ value: string, label: string, hint?: string }[]} options
 */
const SELECT_DROPDOWN_GAP = 6;
const SELECT_DROPDOWN_MAX_HEIGHT = 260;
const SELECT_DROPDOWN_VIEWPORT_PADDING = 8;

const Select = ({
  label,
  error,
  placeholder = '— Выберите —',
  value,
  onChange,
  options = [],
  disabled = false,
  required = false,
  multiple = false,
  id,
}) => {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  const selected = multiple
    ? options.filter((o) => Array.isArray(value) && value.includes(String(o.value)))
    : options.find((o) => String(o.value) === String(value));

  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const maxWidth = viewportWidth - SELECT_DROPDOWN_VIEWPORT_PADDING * 2;
    const width = Math.min(rect.width, maxWidth);
    const left = Math.min(
      Math.max(rect.left, SELECT_DROPDOWN_VIEWPORT_PADDING),
      viewportWidth - width - SELECT_DROPDOWN_VIEWPORT_PADDING
    );
    const availableBelow = viewportHeight - rect.bottom - SELECT_DROPDOWN_GAP - SELECT_DROPDOWN_VIEWPORT_PADDING;
    const availableAbove = rect.top - SELECT_DROPDOWN_GAP - SELECT_DROPDOWN_VIEWPORT_PADDING;
    const openAbove = availableBelow < SELECT_DROPDOWN_MAX_HEIGHT && availableAbove > availableBelow;
    const availableHeight = Math.max(openAbove ? availableAbove : availableBelow, 0);

    setDropdownStyle({
      left,
      width,
      maxHeight: Math.min(SELECT_DROPDOWN_MAX_HEIGHT, availableHeight),
      ...(openAbove
        ? { bottom: viewportHeight - rect.top + SELECT_DROPDOWN_GAP }
        : { top: rect.bottom + SELECT_DROPDOWN_GAP }),
    });
  }, []);

  // Закрытие по клику вне
  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedInsideTrigger = containerRef.current?.contains(e.target);
      const clickedInsideDropdown = dropdownRef.current?.contains(e.target);

      if (!clickedInsideTrigger && !clickedInsideDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Закрытие по Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return undefined;

    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [open, updateDropdownPosition]);

  // ===================================================================================================================
  // = Implementation
  // ===================================================================================================================

  const handleSelect = (optionValue) => {
    if (multiple) {
      const strValue = String(optionValue);
      const current = Array.isArray(value) ? [...value] : [];
      const index = current.indexOf(strValue);
      if (index >= 0) {
        current.splice(index, 1);
      } else {
        current.push(strValue);
      }
      onChange(current);
    } else {
      onChange(String(optionValue));
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  const handleRemoveChip = (chipValue, e) => {
    e.stopPropagation();
    if (!multiple) return;
    const current = Array.isArray(value) ? [...value] : [];
    const index = current.indexOf(String(chipValue));
    if (index >= 0) current.splice(index, 1);
    onChange(current);
  };

  const focusOption = (direction) => {
    const optionButtons = Array.from(dropdownRef.current?.querySelectorAll('.bm-select__option') || []);
    if (optionButtons.length === 0) return;

    const currentIndex = optionButtons.indexOf(document.activeElement);
    const fallbackIndex = options.findIndex((option) => String(option.value) === String(value));
    const nextIndex = currentIndex >= 0
      ? (currentIndex + direction + optionButtons.length) % optionButtons.length
      : Math.max(fallbackIndex, 0);
    optionButtons[nextIndex].focus();
  };

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => focusOption(e.key === 'ArrowDown' ? 1 : -1));
    }
  };

  const handleOptionKeyDown = (e, optionValue) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusOption(e.key === 'ArrowDown' ? 1 : -1);
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect(optionValue);
    }
  };

  const dropdown = open ? (
    <div className="bm-select__dropdown" ref={dropdownRef} role="listbox" style={dropdownStyle}>
      {options.length === 0 ? (
        <div className="bm-select__empty">Нет доступных вариантов</div>
      ) : (
        options.map((option) => {
          const isActive = multiple
            ? Array.isArray(value) && value.includes(String(option.value))
            : String(option.value) === String(value);
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={isActive}
              className={`bm-select__option${isActive ? ' bm-select__option--active' : ''}`}
              onClick={() => handleSelect(option.value)}
              onKeyDown={(e) => handleOptionKeyDown(e, option.value)}
            >
              <span className="bm-select__option-content">
                <span className="bm-select__option-label">{option.label}</span>
                {option.hint && (
                  <span className="bm-select__option-hint">{option.hint}</span>
                )}
              </span>
              {isActive && <PiCheckDuotone size={15} className="bm-select__option-check" />}
            </button>
          );
        })
      )}
    </div>
  ) : null;

  return (
    <div className="bm-input-group" ref={containerRef}>
      {label && (
        <label className="bm-label" htmlFor={inputId}>
          {label}{required && <span className="bm-required-mark"> *</span>}
        </label>
      )}

      {/* Trigger */}
      <button
        id={inputId}
        ref={triggerRef}
        type="button"
        className={['bm-select', error ? 'bm-select--error' : '', open ? 'bm-select--open' : ''].filter(Boolean).join(' ')}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected && (multiple ? selected.length > 0 : true) ? 'bm-select__value' : 'bm-select__placeholder'}>
          {multiple
            ? (selected.length > 0
              ? <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {selected.map((s) => (
                    <span
                      key={s.value}
                      className="bm-select__chip"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '2px 6px',
                        borderRadius: 'var(--bm-radius-sm)',
                        background: 'rgba(0,214,143,0.12)',
                        color: 'var(--bm-accent-green)',
                        fontSize: '0.8em',
                        fontWeight: 600,
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.label}
                      <PiXDuotone
                        size={12}
                        style={{ cursor: 'pointer', flexShrink: 0 }}
                        onClick={(e) => handleRemoveChip(s.value, e)}
                      />
                    </span>
                  ))}
                </span>
              : placeholder)
            : (selected ? selected.label : placeholder)}
        </span>
        <PiCaretDownDuotone
          size={16}
          className={`bm-select__caret${open ? ' bm-select__caret--open' : ''}`}
        />
      </button>

      {error && <span className="bm-input-error-msg">{error}</span>}
      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
};

export default Select;
